// The QR code generator's content and drawing (25 October 2026 rewrite): what each content type encodes, and the
// code with its frame, caption and logo, drawn on a canvas (PNG, preview) or written as SVG. The QR itself comes from
// bwip-js (encoders loaded with import() after the page has painted).

/** The content types, their fields and a sample to open with. */
export const KINDS = [
    { id: 'website', label: 'Website', fields: [{ id: 'url', label: 'Website address', type: 'url', sample: 'https://barkoder.com' }] },
    { id: 'wifi', label: 'Wi-Fi', fields: [
        { id: 'ssid', label: 'Network name', sample: 'barKoder Guest' },
        { id: 'password', label: 'Password', sample: 'scan-me-2026' },
        { id: 'security', label: 'Security', type: 'select', options: ['WPA', 'WEP', 'None'], sample: 'WPA' },
        { id: 'hidden', label: 'Hidden network', type: 'check', sample: false },
    ] },
    { id: 'vcard', label: 'vCard', fields: [
        { id: 'first', label: 'First name', sample: 'Ana' },
        { id: 'last', label: 'Last name', sample: 'Petrova' },
        { id: 'phone', label: 'Phone', type: 'tel', sample: '+389 70 000 000' },
        { id: 'email', label: 'Email', type: 'email', sample: 'ana@example.com' },
        { id: 'company', label: 'Company', sample: 'barKoder' },
        { id: 'title', label: 'Job title', sample: '' },
        { id: 'website', label: 'Website', type: 'url', sample: '' },
        { id: 'address', label: 'Address', sample: '' },
    ] },
    { id: 'email', label: 'Email', fields: [
        { id: 'to', label: 'Email address', type: 'email', sample: 'hello@example.com' },
        { id: 'subject', label: 'Subject', sample: 'Hello from a QR code' },
        { id: 'body', label: 'Message', type: 'textarea', sample: '' },
    ] },
    { id: 'phone', label: 'Phone', fields: [{ id: 'number', label: 'Phone number', type: 'tel', sample: '+389 70 000 000' }] },
];

export const FRAMES = [
    { id: 'none', label: 'None' }, { id: 'label', label: 'Label' }, { id: 'top', label: 'Top' }, { id: 'box', label: 'Box' }, { id: 'badge', label: 'Badge' },
];

const wifiEsc = (s) => String(s || '').replace(/([\\;,:"])/g, '\\$1');
const vEsc = (s) => String(s || '').replace(/([\\;,])/g, '\\$1').replace(/\n/g, '\\n');

/** The text the QR code holds for a content type and its fields; '' when a required field is empty. */
export function payload(kind, f) {
    switch (kind) {
        case 'website': {
            const u = String(f.url || '').trim();
            if (!u) return '';
            return /^[a-z][a-z0-9+.-]*:/i.test(u) ? u : 'https://' + u;
        }
        case 'wifi': {
            if (!String(f.ssid || '').trim()) return '';
            const t = f.security === 'None' ? 'nopass' : f.security;
            return 'WIFI:T:' + t + ';S:' + wifiEsc(f.ssid) + ';' + (t === 'nopass' ? '' : 'P:' + wifiEsc(f.password) + ';') + (f.hidden ? 'H:true;' : '') + ';';
        }
        case 'vcard': {
            if (!String(f.first || '').trim() && !String(f.last || '').trim()) return '';
            const lines = ['BEGIN:VCARD', 'VERSION:3.0', 'N:' + vEsc(f.last) + ';' + vEsc(f.first) + ';;;', 'FN:' + vEsc([f.first, f.last].filter(Boolean).join(' '))];
            if (f.company) lines.push('ORG:' + vEsc(f.company));
            if (f.title) lines.push('TITLE:' + vEsc(f.title));
            if (f.phone) lines.push('TEL;TYPE=CELL:' + vEsc(f.phone));
            if (f.email) lines.push('EMAIL:' + vEsc(f.email));
            if (f.website) lines.push('URL:' + vEsc(f.website));
            if (f.address) lines.push('ADR;TYPE=WORK:;;' + vEsc(f.address) + ';;;;');
            lines.push('END:VCARD');
            return lines.join('\n');
        }
        case 'email': {
            const to = String(f.to || '').trim();
            if (!to) return '';
            const q = [];
            if (f.subject) q.push('subject=' + encodeURIComponent(f.subject));
            if (f.body) q.push('body=' + encodeURIComponent(f.body));
            return 'mailto:' + to + (q.length ? '?' + q.join('&') : '');
        }
        case 'phone': {
            const n = String(f.number || '').replace(/[^\d+]/g, '');
            return n ? 'tel:' + n : '';
        }
    }
    return '';
}

/** Where everything goes, in pixels, for a QR of side q. */
export function layout(frame, q, hasText) {
    const pad = Math.round(q * 0.06);
    const label = Math.round(q * 0.2);
    const t = hasText ? label : 0;
    switch (frame) {
        case 'label': return { w: q + 2 * pad, h: q + 2 * pad + t, qx: pad, qy: pad, radius: pad * 1.6, text: { x: q / 2 + pad, y: q + 2 * pad + t / 2 - pad * 0.15, size: label * 0.46 }, pad };
        case 'top': return { w: q + 2 * pad, h: q + 2 * pad + t, qx: pad, qy: pad + t, radius: pad * 1.6, text: { x: q / 2 + pad, y: t / 2 + pad * 0.85, size: label * 0.46 }, pad };
        case 'box': return { w: q + 2 * pad, h: q + 2 * pad + t, qx: pad, qy: pad, radius: pad, text: { x: q / 2 + pad, y: q + pad + t / 2, size: label * 0.42 }, pad };
        case 'badge': return { w: q, h: q + (t ? t + pad : 0), qx: 0, qy: 0, radius: 0, text: { x: q / 2, y: q + pad + t / 2, size: label * 0.42 }, pad, badge: { x: q * 0.12, y: q + pad, w: q * 0.76, h: t } };
        default: return { w: q, h: q, qx: 0, qy: 0, radius: 0, text: null, pad };
    }
}

const hex = (c) => '#' + String(c).replace(/^#/, '');
function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}
const FONT = '"PlusJakartaSans", "PlusJakartaSans Fallback", Arial, sans-serif';

/**
 * The finished code on a canvas. qrCanvas: the QR drawn by bwip-js; o: { frame, text, fg, bg (null: transparent),
 * logo (an Image or null) }.
 */
export function compose(canvas, qrCanvas, o) {
    const q = qrCanvas.width;
    const L = layout(o.frame, q, o.frame !== 'none' && !!o.text);
    canvas.width = Math.round(L.w);
    canvas.height = Math.round(L.h);
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const fg = hex(o.fg), bg = o.bg ? hex(o.bg) : null, light = bg || '#FFFFFF';
    if (o.frame === 'label' || o.frame === 'top') {
        ctx.fillStyle = fg; roundRect(ctx, 0, 0, L.w, L.h, L.radius); ctx.fill();
        ctx.fillStyle = light; roundRect(ctx, L.qx - L.pad * 0.35, L.qy - L.pad * 0.35, q + L.pad * 0.7, q + L.pad * 0.7, L.pad * 0.8); ctx.fill();
    } else if (o.frame === 'box') {
        if (bg) { ctx.fillStyle = bg; roundRect(ctx, 0, 0, L.w, L.h, L.radius); ctx.fill(); }
        ctx.strokeStyle = fg; ctx.lineWidth = Math.max(2, L.pad * 0.35); roundRect(ctx, ctx.lineWidth / 2, ctx.lineWidth / 2, L.w - ctx.lineWidth, L.h - ctx.lineWidth, L.radius); ctx.stroke();
    } else if (bg) {
        ctx.fillStyle = bg; ctx.fillRect(0, 0, L.w, L.h);
    }
    ctx.drawImage(qrCanvas, L.qx, L.qy);
    if (o.logo) {
        const s = q * 0.22, x = L.qx + (q - s) / 2, y = L.qy + (q - s) / 2;
        ctx.fillStyle = light; roundRect(ctx, x - s * 0.08, y - s * 0.08, s * 1.16, s * 1.16, s * 0.16); ctx.fill();
        const r = Math.min(s / o.logo.naturalWidth, s / o.logo.naturalHeight);
        const lw = o.logo.naturalWidth * r, lh = o.logo.naturalHeight * r;
        ctx.drawImage(o.logo, x + (s - lw) / 2, y + (s - lh) / 2, lw, lh);
    }
    if (L.text) {
        if (L.badge) { ctx.fillStyle = fg; roundRect(ctx, L.badge.x, L.badge.y, L.badge.w, L.badge.h, L.badge.h / 2); ctx.fill(); }
        ctx.fillStyle = o.frame === 'box' ? fg : light;
        ctx.font = '700 ' + Math.round(L.text.size) + 'px ' + FONT;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(o.text, L.text.x, L.text.y, L.w - L.pad * 2);
    }
    return canvas;
}

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The finished code as an SVG document. qrSvg: the QR from bwip-js (toSVG); q: its side in pixels; o as compose(), logo as a data URL. */
export function composeSVG(qrSvg, q, o) {
    const L = layout(o.frame, q, o.frame !== 'none' && !!o.text);
    const fg = hex(o.fg), bg = o.bg ? hex(o.bg) : null, light = bg || '#FFFFFF';
    const inner = qrSvg.replace(/^<svg/, '<svg x="' + L.qx + '" y="' + L.qy + '" width="' + q + '" height="' + q + '"');
    const parts = [];
    if (o.frame === 'label' || o.frame === 'top') {
        parts.push('<rect width="' + L.w + '" height="' + L.h + '" rx="' + L.radius + '" fill="' + fg + '"/>');
        parts.push('<rect x="' + (L.qx - L.pad * 0.35) + '" y="' + (L.qy - L.pad * 0.35) + '" width="' + (q + L.pad * 0.7) + '" height="' + (q + L.pad * 0.7) + '" rx="' + L.pad * 0.8 + '" fill="' + light + '"/>');
    } else if (o.frame === 'box') {
        const lw = Math.max(2, L.pad * 0.35);
        parts.push('<rect x="' + lw / 2 + '" y="' + lw / 2 + '" width="' + (L.w - lw) + '" height="' + (L.h - lw) + '" rx="' + L.radius + '" fill="' + (bg || 'none') + '" stroke="' + fg + '" stroke-width="' + lw + '"/>');
    } else if (bg) {
        parts.push('<rect width="' + L.w + '" height="' + L.h + '" fill="' + bg + '"/>');
    }
    parts.push(inner);
    if (o.logo) {
        const s = q * 0.22, x = L.qx + (q - s) / 2, y = L.qy + (q - s) / 2;
        parts.push('<rect x="' + (x - s * 0.08) + '" y="' + (y - s * 0.08) + '" width="' + s * 1.16 + '" height="' + s * 1.16 + '" rx="' + s * 0.16 + '" fill="' + light + '"/>');
        parts.push('<image href="' + esc(o.logo) + '" x="' + x + '" y="' + y + '" width="' + s + '" height="' + s + '" preserveAspectRatio="xMidYMid meet"/>');
    }
    if (L.text) {
        if (L.badge) parts.push('<rect x="' + L.badge.x + '" y="' + L.badge.y + '" width="' + L.badge.w + '" height="' + L.badge.h + '" rx="' + L.badge.h / 2 + '" fill="' + fg + '"/>');
        parts.push('<text x="' + L.text.x + '" y="' + L.text.y + '" fill="' + (o.frame === 'box' ? fg : light) + '" font-family="Plus Jakarta Sans, Arial, sans-serif" font-weight="700" font-size="' + Math.round(L.text.size) + '" text-anchor="middle" dominant-baseline="middle">' + esc(o.text) + '</text>');
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + L.w + ' ' + L.h + '" width="' + L.w + '" height="' + L.h + '">' + parts.join('') + '</svg>';
}
